import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

// Directly use the backend URL to avoid proxy issues
const API_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;
// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Add withCredentials for CORS requests with credentials
  withCredentials: false
});

// Add request interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API services
export const authService = {
  login: async (email, password) => {
    console.log(API_URL);
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Two-step verification with an authenticator app
export const twoFactorService = {
  status: async () => (await api.get('/auth/2fa')).data,
  setup: async () => (await api.post('/auth/2fa/setup')).data,
  enable: async (code) => (await api.post('/auth/2fa/enable', { code })).data,
  disable: async (password, code) => (await api.post('/auth/2fa/disable', { password, code })).data,
  regenerateRecoveryCodes: async (code) => (await api.post('/auth/2fa/recovery-codes', { code })).data,
  // Admin: turn it off for a user who lost their phone and recovery codes
  reset: async (userId) => (await api.delete(`/users/${userId}/2fa`)).data
};

// WhatsApp Account API services
export const profileService = {
  updateProfile: async (data) => {
    const response = await api.put('/auth/me', data);
    return response.data;
  },
  changePassword: async (data) => {
    const response = await api.put('/auth/password', data);
    return response.data;
  }
};

export const invoiceService = {
  list: async (params) => {
    const response = await api.get('/invoices', { params });
    return response.data;
  },
  startGenerate: async (month, { dryRun = true, accountIds, type = 'monthly' } = {}) => {
    const response = await api.post('/invoices/generate', { month, dryRun, accountIds, type });
    return response.data;
  },
  getGenerateJob: async (jobId) => {
    const response = await api.get(`/invoices/generate/${jobId}`);
    return response.data;
  },
  markPaid: async (id, data) => {
    const response = await api.put(`/invoices/${id}/pay`, data);
    return response.data;
  },
  setStatus: async (id, status) => {
    const response = await api.put(`/invoices/${id}/status`, { status });
    return response.data;
  },
  planRevenue: async (months = 12) => {
    const response = await api.get('/invoices/plan-revenue', { params: { months } });
    return response.data;
  },
  // kind: 'monthly' (message charges), 'subscription' (plan fees), or omitted for both
  paymentsSummary: async (month, kind) => {
    const response = await api.get('/invoices/summary', { params: { month, ...(kind ? { kind } : {}) } });
    return response.data;
  },
  bulkStatus: async (ids, status, payment = {}) => {
    const response = await api.put('/invoices/bulk-status', { ids, status, ...payment });
    return response.data;
  },
  // Returns the CSV as a Blob for download
  exportTallyCsv: async (params) => {
    const response = await api.get('/invoices/export', { params, responseType: 'blob' });
    return response.data;
  }
};

// Servers we rent and the client installs on them
export const serverService = {
  list: async () => (await api.get('/servers')).data,
  get: async (id) => (await api.get(`/servers/${id}`)).data,
  create: async (data) => (await api.post('/servers', data)).data,
  update: async (id, data) => (await api.put(`/servers/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/servers/${id}`)).data
};

export const hostedAppService = {
  // The signed-in customer's own apps: name and domain only
  mine: async () => (await api.get('/hosted-apps/mine')).data,
  create: async (data) => (await api.post('/hosted-apps', data)).data,
  update: async (id, data) => (await api.put(`/hosted-apps/${id}`, data)).data,
  remove: async (id) => (await api.delete(`/hosted-apps/${id}`)).data
};

// What administrators changed, kept for 30 days
export const auditService = {
  list: async (params) => {
    const response = await api.get('/admin/audit', { params });
    return response.data;
  },
  // Returns the CSV as a Blob for download
  exportCsv: async (params) => {
    const response = await api.get('/admin/audit/export', { params, responseType: 'blob' });
    return response.data;
  }
};

export const rateService = {
  getRates: async () => {
    const response = await api.get('/rates');
    return response.data;
  },
  saveCountryRates: async (country, rates) => {
    const response = await api.put(`/rates/${country}`, { rates });
    return response.data;
  },
  deleteCountryRates: async (country) => {
    const response = await api.delete(`/rates/${country}`);
    return response.data;
  }
};

export const whatsappAccountService = {
  getAccounts: async (page = 1, limit = 10, search = '', filters = {}) => {
    const params = { page, limit, search };
    if (filters.status) params.status = filters.status;
    if (filters.verification) params.verification = filters.verification;
    const response = await api.get('/whatsapp-accounts', { params });
    return response.data;
  },
  getAccount: async (id) => {
    const response = await api.get(`/whatsapp-accounts/${id}`);
    return response.data;
  },
  createAccount: async (accountData) => {
    const response = await api.post('/whatsapp-accounts', accountData);
    return response.data;
  },
  updateAccount: async (id, accountData) => {
    const response = await api.put(`/whatsapp-accounts/${id}`, accountData);
    return response.data;
  },
  deleteAccount: async (id) => {
    const response = await api.delete(`/whatsapp-accounts/${id}`);
    return response.data;
  },
  connectWhatsAppAccount: async (accountData) => {
    const response = await api.post('/whatsapp-accounts/connect', accountData);
    return response.data;
  },
  connectBusinessAppAccount: async (accountData) => {
    const response = await api.post('/whatsapp-accounts/connect-business-app', accountData);
    return response.data;
  },
  refreshAccount: async (id) => {
    const response = await api.put(`/whatsapp-accounts/${id}/refresh`);
    return response.data;
  },
  getAccountToken: async (id) => {
    const response = await api.get(`/whatsapp-accounts/${id}/token`);
    return response.data;
  },
  getAccountAnalytics: async (id, params) => {
    const { startDate, endDate, granularity } = params;
    const response = await api.get(
      `/whatsapp-accounts/${id}/analytics?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`
    );
    return response.data;
  },
  getConversationAnalytics: async (id, params) => {
    const { startDate, endDate, granularity } = params;
    const response = await api.get(
      `/whatsapp-accounts/${id}/conversation-analytics?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`
    );
    return response.data;
  },
  getPricingAnalytics: async (id, params) => {
    const { startDate, endDate, granularity } = params;
    const response = await api.get(
      `/whatsapp-accounts/${id}/pricing-analytics?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`
    );
    return response.data;
  },
  getTemplatePerformance: async (id, { templateIds, start, end }) => {
    const response = await api.get(`/whatsapp-accounts/${id}/templates/analytics`, {
      params: { templateIds, start, end },
      timeout: 120000
    });
    return response.data;
  },
  getMetaTemplates: async (id) => {
    const response = await api.get(`/whatsapp-accounts/${id}/templates`);
    return response.data;
  },
  createMetaTemplate: async (id, template, { validateOnly = false } = {}) => {
    const response = await api.post(`/whatsapp-accounts/${id}/templates`, template, {
      params: validateOnly ? { validate: true } : {}
    });
    return response.data;
  },
  uploadTemplateMedia: async (id, file) => {
    const response = await api.post(`/whatsapp-accounts/${id}/templates/media`, file, {
      headers: { 'Content-Type': file.type },
      params: { fileName: file.name },
      timeout: 180000
    });
    return response.data;
  },
  deleteMetaTemplate: async (id, { name, hsmId }) => {
    const response = await api.delete(`/whatsapp-accounts/${id}/templates`, { params: { name, hsmId } });
    return response.data;
  },
  getAnalyticsSummary: async ({ refresh = false } = {}) => {
    const response = await api.get('/whatsapp-accounts/analytics-summary', { params: refresh ? { refresh: true } : {} });
    return response.data;
  }
};

// Message API services
export const messageService = {
  getMessages: async (page = 1, limit = 20) => {
    const response = await api.get(`/messages?page=${page}&limit=${limit}`);
    return response.data;
  },
  getMessage: async (id) => {
    const response = await api.get(`/messages/${id}`);
    return response.data;
  },
  sendMessage: async (messageData) => {
    const response = await api.post('/messages', messageData);
    return response.data;
  }
};

// Template API services
export const templateService = {
  getTemplates: async () => {
    const response = await api.get('/templates');
    return response.data;
  },
  getTemplate: async (id) => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },
  createTemplate: async (templateData) => {
    const response = await api.post('/templates', templateData);
    return response.data;
  },
  updateTemplate: async (id, templateData) => {
    const response = await api.put(`/templates/${id}`, templateData);
    return response.data;
  },
  deleteTemplate: async (id) => {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  }
};

// Billing API services
export const billingService = {
  createBilling: async (data) => {
    const response = await api.post('/billing', data);
    return response.data;
  },
  getBillings: async () => {
    const response = await api.get('/billing');
    return response.data;
  },
  getAllBillings: async (params) => {
    const response = await api.get('/billing/all', { params });
    return response.data;
  },
  getBilling: async (id) => {
    const response = await api.get(`/billing/${id}`);
    return response.data;
  },
  updateBilling: async (id, data) => {
    const response = await api.put(`/billing/${id}`, data);
    return response.data;
  },
  deleteBilling: async (id) => {
    const response = await api.delete(`/billing/${id}`);
    return response.data;
  },
  // effective: 'period_end' (default), 'month_end' or 'immediate'
  cancelSubscription: async (id, effective) => {
    const response = await api.put(`/billing/${id}/cancel`, effective ? { effective } : {});
    return response.data;
  },
  getInvoices: async () => {
    const response = await api.get('/billing/invoices');
    return response.data;
  },
  generateInvoice: async (id, data) => {
    const response = await api.post(`/billing/${id}/invoice`, data);
    return response.data;
  },
  processPayment: async (id, data) => {
    const response = await api.put(`/billing/${id}/process`, data);
    return response.data;
  },
  processMonthlyRenewals: async () => {
    const response = await api.post('/billing/process-renewals');
    return response.data;
  },
  generatePdfUrl: (invoiceId) => {
    const apiUrl = getApiUrl();
    const token = localStorage.getItem('token');
    // URL encode the token to handle special characters
    const encodedToken = encodeURIComponent(token);
    return `${apiUrl}/invoices/${invoiceId}/pdf?token=${encodedToken}`;
  }
};

// Plan API services
export const planService = {
  getPlans: async (params) => {
    const response = await api.get('/plans', { params });
    return response.data;
  },
  getPlan: async (id) => {
    const response = await api.get(`/plans/${id}`);
    return response.data;
  },
  createPlan: async (planData) => {
    const response = await api.post('/plans', planData);
    return response.data;
  },
  updatePlan: async (id, planData) => {
    const response = await api.put(`/plans/${id}`, planData);
    return response.data;
  },
  deletePlan: async (id) => {
    const response = await api.delete(`/plans/${id}`);
    return response.data;
  }
};

// User API services
export const userService = {
  getUsers: async (params) => {
    const response = await api.get('/users', { params });
    return response.data;
  },
  getUser: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  getUserDetails: async (id) => {
    const response = await api.get(`/users/${id}/details`);
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  updateUser: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

// Notification service
export const notificationService = {
  getNotifications: (params) => api.get('/notifications', { params }),
  getNotification: (id) => api.get(`/notifications/${id}`),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`)
};

export default api;
